import { useMemo } from "react";
import { TeamLeaderReportPreviewRecipient } from "app/containers/WeeklyCheckInMonitor/types";
import {
  useGetTeamLeaderSettingsQuery,
  useGetTeamLeaderPreviewQuery,
  useUpdateTeamLeaderSettingsMutation,
  useGetSeniorManagerPreviewQuery,
} from "./weeklyCheckInReportSettingsApi";
import ReportPreviewDrawer from "./ReportPreviewDrawer";

interface TeamLeaderReportPreviewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onEditSettings?: () => void;
}

const renderSubtitle = (recipient: TeamLeaderReportPreviewRecipient) => {
  const teamLabel =
    recipient.teamCount === 1 ? "1 team" : `${recipient.teamCount} teams`;
  return (
    <>
      {recipient.jobTitle ?? ""}
      {recipient.teamCount > 0 && ` · ${teamLabel}`}
      {recipient.teamMemberCount > 0 &&
        ` · ${recipient.teamMemberCount} members`}
      {recipient.percentInvited > 0 && ` · ${recipient.percentInvited}%`}
    </>
  );
};

export default function TeamLeaderReportPreviewDrawer({
  isOpen,
  onClose,
  onEditSettings,
}: Readonly<TeamLeaderReportPreviewDrawerProps>) {
  const { data: preview, isLoading } = useGetTeamLeaderPreviewQuery();
  const { data: settings } = useGetTeamLeaderSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateTeamLeaderSettingsMutation();
  const { data: seniorManagerPreview } = useGetSeniorManagerPreviewQuery();

  const seniorManagerRecipientIds = useMemo(() => {
    if (!seniorManagerPreview?.recipients) return new Set<number>();
    return new Set(
      seniorManagerPreview.recipients
        .filter(
          (r) =>
            r.status === "qualified_by_rules" ||
            r.status === "included_override"
        )
        .map((r) => r.userAccountId)
    );
  }, [seniorManagerPreview]);

  return (
    <ReportPreviewDrawer
      isOpen={isOpen}
      onClose={onClose}
      reportName="Team Leader Check-in Report"
      preview={preview}
      settings={settings}
      isLoading={isLoading}
      isSaving={isSaving}
      updateSettings={updateSettings}
      renderRecipientSubtitle={renderSubtitle}
      recipientRoleLabel="Team Leader"
      coverageSubject={{
        group: "members of the teams they lead",
        member: "member of the teams they lead",
      }}
      onEditSettings={onEditSettings}
      seniorManagerRecipientIds={seniorManagerRecipientIds}
    />
  );
}
